import {
  BookOpen,
  ClipboardList,
  CalendarClock,
  CreditCard,
  HeartPulse,
  History,
  KeyRound,
  LayoutDashboard,
  Monitor,
  PackageSearch,
  Receipt,
  Settings as SettingsIcon,
  ShieldCheck,
  Stethoscope,
  Tag,
  Users,
  Wallet,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import { TotemMarkOutline } from '@/shared/brand/TotemLogo';
import { cn } from '@/shared/lib/cn';
import type { AuthUser } from '@/stores/authStore';

import { useDemoMode } from './useDemoMode';

interface NavItem {
  to: string;
  label_key: string;
  icon: typeof LayoutDashboard;
  /** ANY of these permissions grants visibility. Empty = visible to anyone. */
  any_permission?: ReadonlyArray<string>;
  /** Shows a "+ PLUS" badge when `demo_mode` is on. Purely cosmetic. */
  is_plus?: boolean;
}

interface NavGroup {
  label_key: string;
  items: ReadonlyArray<NavItem>;
}

const NAV_GROUPS: ReadonlyArray<NavGroup> = [
  {
    label_key: 'nav.group_operations',
    items: [
      { to: '/app/dashboard', label_key: 'nav.dashboard', icon: LayoutDashboard, any_permission: ['admin.dashboard.view'] },
      { to: '/app/cashier', label_key: 'nav.cashier_pos', icon: CreditCard, any_permission: ['pos.cashier.use'] },
      { to: '/app/sales-history', label_key: 'nav.sales_history', icon: History, any_permission: ['pos.cashier.use'] },
      { to: '/app/doctor', label_key: 'nav.doctor_pos', icon: Stethoscope, any_permission: ['pos.doctor.use'], is_plus: true },
      { to: '/app/customers', label_key: 'nav.customers', icon: Users, any_permission: ['customers.read'] },
      { to: '/app/display/waiting-room', label_key: 'nav.waiting_room', icon: Monitor, any_permission: ['display.waiting_room.view'], is_plus: true },
      { to: '/app/display/medical-queue', label_key: 'nav.medical_queue', icon: Monitor, any_permission: ['display.medical_queue.view'], is_plus: true },
    ],
  },
  {
    label_key: 'nav.group_clinical',
    items: [
      { to: '/app/clinical', label_key: 'nav.clinical', icon: Stethoscope, any_permission: ['clinical.visits.write'] },
      { to: '/app/hospitalization', label_key: 'nav.hospitalization', icon: HeartPulse, any_permission: ['hospitalization.records.read'], is_plus: true },
      { to: '/app/appointments', label_key: 'nav.appointments', icon: CalendarClock, any_permission: ['appointments.read'] },
    ],
  },
  {
    label_key: 'nav.group_catalog',
    items: [
      { to: '/app/catalog', label_key: 'nav.catalog', icon: PackageSearch, any_permission: ['catalog.products.read', 'catalog.services.read'] },
      { to: '/app/inventory', label_key: 'nav.inventory', icon: ClipboardList, any_permission: ['inventory.read'] },
      { to: '/app/pricing', label_key: 'nav.pricing', icon: Tag, any_permission: ['pricing.rules.read'] },
    ],
  },
  {
    label_key: 'nav.group_finance',
    items: [
      { to: '/app/cash', label_key: 'nav.cash', icon: Wallet, any_permission: ['cash.shift.open', 'cash.movement.cash_in', 'pos.cashier.use'] },
      { to: '/app/open-accounts', label_key: 'nav.open_accounts', icon: BookOpen, any_permission: ['open_accounts.read'], is_plus: true },
      { to: '/app/invoicing', label_key: 'nav.invoicing', icon: Receipt, any_permission: ['invoicing.cfdi.request'] },
    ],
  },
  {
    label_key: 'nav.group_admin',
    items: [
      { to: '/app/audit', label_key: 'nav.audit', icon: ShieldCheck, any_permission: ['audit.view'], is_plus: true },
      { to: '/app/otp', label_key: 'nav.otp_admin', icon: KeyRound, any_permission: ['admin.users.manage'], is_plus: true },
      { to: '/app/users', label_key: 'nav.users_admin', icon: Users, any_permission: ['admin.users.manage'] },
      { to: '/app/settings', label_key: 'nav.settings', icon: SettingsIcon, any_permission: ['admin.users.manage'] },
    ],
  },
];

function canSee(item: NavItem, user: AuthUser): boolean {
  if (!item.any_permission || item.any_permission.length === 0) return true;
  return item.any_permission.some((p) => user.permissions.includes(p));
}

interface SidebarProps {
  user: AuthUser;
}

export function Sidebar({ user }: SidebarProps): JSX.Element {
  const { t } = useTranslation();
  const demo_mode = useDemoMode();

  const visible_groups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => canSee(item, user)),
  })).filter((group) => group.items.length > 0);

  return (
    <aside className="totem-rail hidden w-64 shrink-0 flex-col text-sidebar-foreground md:flex">
      <div className="flex items-center gap-3 px-5 pb-5 pt-5">
        <TotemMarkOutline className="h-9 w-auto text-white" title={t('common.app_name')} />
        <div className="leading-none">
          <span className="font-display text-lg font-extrabold tracking-tight text-white">tótem</span>
          <span className="mt-1 block text-[9px] font-semibold uppercase tracking-[0.18em] text-sidebar-muted">
            {t('common.tagline')}
          </span>
        </div>
      </div>

      <nav className="scrollbar-none flex-1 space-y-5 overflow-y-auto px-3 pb-6">
        {visible_groups.map((group) => (
          <div key={group.label_key} className="space-y-1">
            <p className="px-3 pb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-sidebar-muted">
              {t(group.label_key)}
            </p>
            {group.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-white/15 text-white shadow-sm'
                      : 'text-sidebar-foreground/75 hover:bg-white/10 hover:text-white',
                  )
                }
              >
                <item.icon className="h-[18px] w-[18px] shrink-0" aria-hidden />
                <span className="truncate">{t(item.label_key)}</span>
                {item.is_plus && demo_mode ? (
                  <span
                    className="ml-auto inline-flex shrink-0 items-center rounded-full bg-gradient-to-r from-amber-400 to-orange-500 px-1.5 py-0.5 text-[9px] font-extrabold uppercase leading-none text-white shadow-sm"
                    title={t('nav.plus_badge_title')}
                  >
                    + Plus
                  </span>
                ) : null}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
    </aside>
  );
}
